import React from "react"
import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import MediaContainer from "../common/MediaContainer"
import { useConfig } from "../../hooks/useConfig"
import * as ColorUtil from "../../utils/ColorUtil"

interface UploadViewProps {
  onImageSelected: (file: File) => void
}

export default function UploadView({ onImageSelected }: UploadViewProps) {
  const { t } = useTranslation()
  const theme = useConfig().theme

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/jpeg": [], "image/png": [] },
    multiple: false,
    onDropAccepted: (files) => {
      if (files[0]) onImageSelected(files[0])
    },
  })

  const borderColor = isDragActive
    ? theme.textColor
    : ColorUtil.hexToCssRgba(theme.textColor, 0.4)

  return (
    <MediaContainer>
      <Box
        {...getRootProps()}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          border: `2px dashed ${borderColor}`,
          borderRadius: 2,
          m: 1.5,
          transition: "border-color 0.2s, background-color 0.2s",
          bgcolor: isDragActive ? ColorUtil.hexToCssRgba(theme.textColor, 0.08) : "transparent",
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadOutlinedIcon
          sx={{ fontSize: 56, color: theme.textColor, mb: 1.5 }}
        />
        <Typography sx={{ color: theme.textColor, fontSize: "1.1rem", fontWeight: 500 }}>
          {t("capture.dropzone.heading")}
        </Typography>
        <Typography sx={{ color: theme.textColor, fontSize: "0.9rem", opacity: 0.7, mt: 0.5 }}>
          {t("capture.dropzone.subheading")}
        </Typography>
      </Box>
    </MediaContainer>
  )
}
